import { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, Zap, Brain, Server, Globe, SwatchBook, Rocket, ExternalLink, Activity, Users, BookOpen, Terminal, Wifi, WifiOff, ChevronDown, ChevronUp } from 'lucide-react'

const otherAgents = [
  { id: 'space-agent', label: 'Space Agent', url: 'https://github.com/agent0ai/space-agent', color: 'aith-purple', desc: 'Agent framework' },
  { id: 'openhuman', label: 'OpenHuman', url: 'https://github.com/tinyhumansai/openhuman', color: 'aith-accent', desc: 'Human-like AI' },
  { id: 'aionui', label: 'AionUI', url: 'https://github.com/iOfficeAI/AionUi', color: 'aith-green', desc: 'AI UI builder' },
]

const wuphfAgents = [
  { role: 'CEO', name: 'Chief Executive', status: 'idle', color: 'aith-accent' },
  { role: 'PM', name: 'Product Manager', status: 'idle', color: 'aith-purple' },
  { role: 'Eng', name: 'Engineer', status: 'idle', color: 'aith-green' },
  { role: 'Design', name: 'Designer', status: 'idle', color: 'aith-orange' },
  { role: 'CMO', name: 'Marketing', status: 'idle', color: 'aith-yellow' },
  { role: 'CRO', name: 'Revenue', status: 'idle', color: 'aith-red' },
]

const brains = [
  { id: 'local', label: 'LOCAL', icon: Server, color: 'aith-green', desc: 'Ollama on Stomach', endpoint: 'local' },
  { id: 'typhoon', label: 'TYPHOON', icon: Globe, color: 'aith-orange', desc: 'Thai compliance brain', endpoint: 'hf_api' },
  { id: 'athena', label: 'ATHENA', icon: Brain, color: 'aith-purple', desc: 'Qwen/Claude 40B Space', endpoint: 'hf_space' },
  { id: 'kimi', label: 'KIMI', icon: SwatchBook, color: 'aith-accent', desc: 'K2.6 Swarm Space', endpoint: 'hf_space' },
]

export default function FalconChat() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Falcon AI online. Stomach (Local) | Typhoon (HF API) | Athena Space | Kimi Space. Select a brain. How can I assist, Solo?' }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [activeBrain, setActiveBrain] = useState('local')
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMessage = async () => {
    if (!input.trim()) return
    const userMsg = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userMsg }])
    setLoading(true)

    // BRAIN ROUTING: Route to the selected brain
    if (activeBrain === 'athena' || activeBrain === 'kimi') {
      // HF Space endpoints (Athena or Kimi) — configured by HF Director
      const spaceConfig = {
        athena: { spaceUrl: 'https://aithaiai-athena.hf.space/api/generate', model: 'Qwen 3.6 40B' },
        kimi: { spaceUrl: 'https://aithaiai-kimi.hf.space/api/generate', model: 'Kimi K2.6' }
      }
      const config = spaceConfig[activeBrain]
      try {
        const res = await fetch('https://thisisspartaaa.aithai.workers.dev/space', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            space_url: config.spaceUrl,
            messages: [...messages, { role: 'user', content: userMsg }].filter(m => m.role !== 'assistant' || !m.content.includes('Falcon AI online')),
            temperature: 0.7,
            max_tokens: 1024,
            brain: activeBrain
          })
        })
        const data = await res.json()
        if (data.content) {
          setMessages(prev => [...prev, { role: 'assistant', content: data.content, model: data.model || config.model, mode: activeBrain }])
        } else if (data.error) {
          setMessages(prev => [...prev, { role: 'assistant', content: `${activeBrain.toUpperCase()}: ${data.error}`, mode: `${activeBrain}-error` }])
        } else {
          setMessages(prev => [...prev, { role: 'assistant', content: `${activeBrain} responded in an unexpected format.`, mode: `${activeBrain}-error` }])
        }
        setLoading(false)
        return
      } catch (e) {
        setMessages(prev => [...prev, { role: 'assistant', content: `${activeBrain.toUpperCase()} connection failed: ${e.message}`, mode: `${activeBrain}-error` }])
        setLoading(false)
        return
      }
    }

    if (activeBrain === 'typhoon') {
      // HF Inference API — Typhoon Thai model
      try {
        const res = await fetch('https://thisisspartaaa.aithai.workers.dev/athena', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [...messages, { role: 'user', content: userMsg }].filter(m => m.role !== 'assistant' || !m.content.includes('Falcon AI online')),
            temperature: 0.7,
            max_tokens: 1024,
            brain: 'typhoon',
            model_override: 'typhoon-ai/typhoon2.5-qwen3-30b-a3b'
          })
        })
        const data = await res.json()
        if (data.content) {
          setMessages(prev => [...prev, { role: 'assistant', content: data.content, model: data.model, mode: 'typhoon' }])
        } else if (data.error) {
          setMessages(prev => [...prev, { role: 'assistant', content: `Typhoon: ${data.error}`, mode: 'typhoon-error' }])
        } else {
          setMessages(prev => [...prev, { role: 'assistant', content: 'Typhoon responded in an unexpected format.', mode: 'typhoon-error' }])
        }
        setLoading(false)
        return
      } catch (e) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Typhoon connection failed: ${e.message}`, mode: 'typhoon-error' }])
        setLoading(false)
        return
      }
    }

    // LOCAL MODE: Try Stomach Ollama
    try {
      const res = await fetch('http://100.122.20.113:11434/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'llama3.1',
          prompt: userMsg,
          stream: false
        })
      })
      const data = await res.json()
      if (data.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response, mode: 'local' }])
        setLoading(false)
        return
      }
    } catch (e) {
      // Ollama failed, try Cloudflare Worker fallback
    }

    // Fallback: Use existing Worker
    try {
      const res = await fetch('https://thisisspartaaa.aithai.workers.dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg })
      })
      const data = await res.json()
      setMessages(prev => [...prev, { role: 'assistant', content: data.response || 'Worker responded but no text returned.', mode: 'worker' }])
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection failed. Stomach (Ollama) and Cloudflare Worker both unreachable. Check network.', mode: 'error' }])
    }
    setLoading(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const currentBrain = brains.find(b => b.id === activeBrain)
  const CurrentIcon = currentBrain?.icon || Brain

  const [wuphfPanelOpen, setWuphfPanelOpen] = useState(true)
  const [wuphfConnected, setWuphfConnected] = useState(false)
  const [wuphfCommand, setWuphfCommand] = useState('')
  const [wuphfLogs, setWuphfLogs] = useState([
    { time: '12:00:00', agent: 'System', message: 'WUPHF Office ready. Install on Stomach to activate autonomous agents.' }
  ])
  const [showInstall, setShowInstall] = useState(false)

  const openAgent = (url) => {
    window.open(url, '_blank')
  }

  const sendWuphfCommand = () => {
    if (!wuphfCommand.trim()) return
    const cmd = wuphfCommand.trim()
    setWuphfCommand('')
    setWuphfLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), agent: 'Solo', message: cmd }])
    // Simulated response (until real WUPHF is connected)
    setTimeout(() => {
      setWuphfLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), agent: 'WUPHF', message: `Command received: "${cmd}". Install WUPHF on Stomach to execute autonomously.` }])
    }, 500)
  }

  const handleWuphfKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendWuphfCommand()
    }
  }

  return (
    <div className="flex gap-4 h-[calc(100vh-140px)]">
      {/* Main Chat Area — 3/4 */}
      <div className="flex-[3] flex flex-col min-w-0">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-aith-accent" />
          <h2 className="text-xs font-bold tracking-widest text-gray-500 uppercase">Falcon AI Chat</h2>
          <div className="ml-auto flex gap-1">
            {brains.map(b => {
              const BIcon = b.icon
              const isActive = activeBrain === b.id
              return (
                <button
                  key={b.id}
                  onClick={() => setActiveBrain(b.id)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-[9px] font-bold transition-colors ${
                    isActive
                      ? `bg-${b.color}/20 text-${b.color} border border-${b.color}/30`
                      : 'bg-gray-800 text-gray-600 border border-gray-700 hover:text-gray-400'
                  }`}
                  title={`${b.label}: ${b.desc}`}
                >
                  <BIcon className="w-2.5 h-2.5" />
                  {b.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-2 mb-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${
              msg.role === 'user' ? 'bg-aith-accent/20 text-aith-accent' : 'bg-aith-purple/20 text-aith-purple'
            }`}>
              {msg.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
            </div>
            <div className={`max-w-[80%] p-3 rounded-lg text-xs leading-relaxed ${
              msg.role === 'user'
                ? 'bg-aith-accent/10 border border-aith-accent/20 text-gray-200'
                : msg.mode?.endsWith('-error') || msg.mode === 'error'
                  ? 'bg-aith-red/10 border border-aith-red/20 text-gray-300'
                  : msg.mode === 'athena'
                    ? 'bg-aith-purple/10 border border-aith-purple/20 text-gray-300'
                    : msg.mode === 'kimi'
                      ? 'bg-aith-accent/10 border border-aith-accent/20 text-gray-300'
                      : msg.mode === 'typhoon'
                        ? 'bg-aith-orange/10 border border-aith-orange/20 text-gray-300'
                        : 'bg-gray-800/50 border border-gray-700 text-gray-300'
            }`}>
              {msg.content}
              {msg.role === 'assistant' && msg.mode && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700/50">
                  <span className={`text-[9px] uppercase tracking-wider ${
                    msg.mode === 'athena' ? 'text-aith-purple' :
                    msg.mode === 'kimi' ? 'text-aith-accent' :
                    msg.mode === 'typhoon' ? 'text-aith-orange' :
                    msg.mode === 'local' ? 'text-aith-green' :
                    msg.mode === 'worker' ? 'text-aith-cyan' :
                    msg.mode === 'athena-error' || msg.mode === 'kimi-error' || msg.mode === 'typhoon-error' || msg.mode === 'error' ? 'text-aith-red' :
                    'text-gray-600'
                  }`}>
                    {msg.mode === 'athena' ? '◆ Athena Space' :
                     msg.mode === 'kimi' ? '◆ Kimi Space' :
                     msg.mode === 'typhoon' ? '◆ Typhoon (HF)' :
                     msg.mode === 'local' ? '● Ollama (Local)' :
                     msg.mode === 'worker' ? '● Worker AI' :
                     msg.mode === 'athena-error' ? '✕ Athena Error' :
                     msg.mode === 'kimi-error' ? '✕ Kimi Error' :
                     msg.mode === 'typhoon-error' ? '✕ Typhoon Error' :
                     '● Unknown'}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded bg-aith-purple/20 flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-aith-purple animate-pulse" />
            </div>
            <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

        {/* Input */}
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Falcon AI..."
            className="flex-1 bg-aith-panel border border-aith-border rounded-lg px-4 py-2.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-aith-accent resize-none h-10 flex items-center"
            rows={1}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="px-4 py-2 bg-aith-accent text-white rounded-lg hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5" />
            <span className="text-xs">Send</span>
          </button>
        </div>
      </div>

      {/* Agent Sidebar — 1/4 */}
      <div className="flex-1 flex flex-col min-w-[220px] max-w-[280px]">
        {/* WUPHF Office Panel */}
        <div className="mb-3">
          <button
            onClick={() => setWuphfPanelOpen(!wuphfPanelOpen)}
            className="w-full flex items-center justify-between mb-2 group"
          >
            <div className="flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-aith-orange" />
              <h3 className="text-[10px] font-bold tracking-widest text-gray-500 uppercase">WUPHF Office</h3>
              {wuphfConnected ? (
                <Wifi className="w-3 h-3 text-aith-green" />
              ) : (
                <WifiOff className="w-3 h-3 text-gray-600" />
              )}
            </div>
            {wuphfPanelOpen ? (
              <ChevronUp className="w-3 h-3 text-gray-600 group-hover:text-gray-400" />
            ) : (
              <ChevronDown className="w-3 h-3 text-gray-600 group-hover:text-gray-400" />
            )}
          </button>

          {wuphfPanelOpen && (
            <div className="space-y-3">
              {/* Status */}
              <div className={`p-2 rounded border text-center ${
                wuphfConnected
                  ? 'bg-aith-green/5 border-aith-green/20'
                  : 'bg-aith-orange/5 border-aith-orange/20'
              }`}>
                <span className={`text-[10px] font-bold ${wuphfConnected ? 'text-aith-green' : 'text-aith-orange'}`}>
                  {wuphfConnected ? '● Office Online' : '◌ Not Connected'}
                </span>
                <p className="text-[9px] text-gray-500 mt-0.5">
                  {wuphfConnected ? '6 agents active' : 'Install on Stomach to activate'}
                </p>
              </div>

              {/* Agents */}
              <div className="space-y-1">
                {wuphfAgents.map(agent => (
                  <div key={agent.role} className="flex items-center justify-between px-2 py-1 rounded bg-gray-800/40">
                    <div className="flex items-center gap-2">
                      <Users className={`w-3 h-3 text-${agent.color}`} />
                      <span className="text-[10px] text-gray-300">{agent.role}</span>
                    </div>
                    <span className={`text-[9px] ${agent.status === 'active' ? 'text-aith-green' : 'text-gray-600'}`}>
                      {agent.status}
                    </span>
                  </div>
                ))}
              </div>

              {/* Wiki / Memory */}
              <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-gray-800/40 cursor-pointer hover:bg-gray-800/60 transition-colors">
                <BookOpen className="w-3 h-3 text-aith-accent" />
                <span className="text-[10px] text-gray-400">Shared Wiki</span>
                <span className="ml-auto text-[9px] text-gray-600">0 entries</span>
              </div>

              {/* Logs */}
              <div className="bg-aith-bg border border-aith-border rounded p-2 max-h-[120px] overflow-y-auto">
                {wuphfLogs.map((log, i) => (
                  <div key={i} className="text-[9px] mb-1">
                    <span className="text-gray-600">{log.time}</span>{' '}
                    <span className={
                      log.agent === 'Solo' ? 'text-aith-accent' :
                      log.agent === 'WUPHF' ? 'text-aith-orange' :
                      'text-gray-500'
                    }>{log.agent}:</span>{' '}
                    <span className="text-gray-400">{log.message}</span>
                  </div>
                ))}
              </div>

              {/* Command Input */}
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={wuphfCommand}
                  onChange={(e) => setWuphfCommand(e.target.value)}
                  onKeyDown={handleWuphfKeyDown}
                  placeholder="Command WUPHF..."
                  className="flex-1 bg-aith-panel border border-aith-border rounded px-2 py-1.5 text-[10px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-aith-orange"
                />
                <button
                  onClick={sendWuphfCommand}
                  disabled={!wuphfCommand.trim()}
                  className="px-2 py-1.5 bg-aith-orange text-white rounded text-[10px] hover:bg-orange-600 disabled:opacity-40 transition-colors"
                >
                  <Terminal className="w-3 h-3" />
                </button>
              </div>

              {/* Install Toggle */}
              <button
                onClick={() => setShowInstall(!showInstall)}
                className="w-full text-[9px] text-gray-500 hover:text-gray-300 text-left flex items-center gap-1"
              >
                <Terminal className="w-2.5 h-2.5" />
                {showInstall ? 'Hide install steps' : 'How to install on Stomach'}
              </button>
              {showInstall && (
                <div className="bg-aith-bg border border-aith-border rounded p-2 text-[9px] text-gray-500 font-mono space-y-1">
                  <p className="text-aith-orange"># SSH into Stomach</p>
                  <p>ssh 100.122.20.113</p>
                  <p className="text-aith-orange mt-1"># Install WUPHF</p>
                  <p>git clone https://github.com/nex-crm/wuphf.git</p>
                  <p>cd wuphf && go build ./cmd/wuphf</p>
                  <p>./wuphf init</p>
                  <p className="text-aith-orange mt-1"># Start office</p>
                  <p>./wuphf</p>
                  <p className="text-aith-orange mt-1"># Connect Falcon</p>
                  <p>./wuphf share</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-aith-border my-2" />

        {/* Other Agents */}
        <div className="flex items-center gap-2 mb-2">
          <Rocket className="w-3 h-3 text-gray-500" />
          <h3 className="text-[10px] font-bold tracking-widest text-gray-600 uppercase">Other Agents</h3>
        </div>
        <div className="space-y-2">
          {otherAgents.map(agent => (
            <button
              key={agent.id}
              onClick={() => openAgent(agent.url)}
              className="w-full text-left p-2 rounded-lg border border-gray-700/50 bg-gray-800/20 hover:bg-gray-800/40 hover:border-gray-600 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-gray-400 group-hover:text-gray-300">{agent.label}</span>
                <ExternalLink className="w-2.5 h-2.5 text-gray-600 group-hover:text-gray-400" />
              </div>
              <p className="text-[9px] text-gray-600">{agent.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
